/* @flow */

import * as React from 'react';
import { useIntl } from 'react-intl';
import { Field, Formik, Form } from 'formik';

// Styles
import styles from 'src/shared/components/domain/user/form/RecommendationModal.style';
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';

// Utils
import messages from 'src/shared/domain/user/l10n/userL10n';
import { getUserFullName } from '@braindate/domain/lib/user/util';

// Components
import Modal from 'src/shared/app/base/modal/component/Modal';
import RecommendIcon from 'src/shared/ui/component/icon/RecommendIcon';
import PrimaryButton from 'src/shared/ui/component/button/PrimaryButton';
import FormikFormGroup from 'src/shared/app/base/component/data-entry/form/FormikFormGroup';
import FieldUserAutocomplete from './common/FieldUserAutocomplete';

// Types
import type { User } from '@braindate/domain/lib/user/type';

type Props = {
  title: string,
  defaultUser?: User,
  onClose: () => void,
  onSubmit: (values: Object) => Promise<any>,
};

const useStyles = createUseThemeStyles(styles);

const RecommendationModal = ({
  title,
  defaultUser,
  onClose,
  onSubmit,
}: Props): React.Node => {
  const intl = useIntl();
  const classes = useStyles();
  const [step, setStep] = React.useState(1);
  const [selectedUser, setSelectedUser] = React.useState();

  const errorMessage = intl.formatMessage(messages.recommendUserErrorMessage);
  const textErrorMessage = intl.formatMessage(messages.recommendErrorMessage);

  const handleSubmit = (values, { setSubmitting, setStatus, setErrors }) => {
    if (!values.user || !values.message) {
      setErrors({
        user: !values.user ? errorMessage : null,
        message: !values.message ? textErrorMessage : null,
      });
      setSubmitting(false);
    } else {
      onSubmit(values)
        .then(() => {
          setSelectedUser(values.user);
          setStep(2);
        })
        .catch((e) => {
          setSubmitting(false);
          setStatus(e);
          throw e;
        });
    }
  };

  const step1 = (
    <Formik
      initialValues={{
        message: '',
        user: defaultUser || '',
      }}
      onSubmit={handleSubmit}
    >
      {({ values, isSubmitting }) => (
        <Form className={classes.form} noValidate>
          <FieldUserAutocomplete
            label={intl.formatMessage(
              !defaultUser ? messages.recommendLabel : messages.recommendTo,
            )}
            suppParams={{ people_tab_optin: true }}
            disabled={!!defaultUser}
          />
          <Field
            id="message"
            name="message"
            rows="5"
            required
            component={FormikFormGroup}
            componentOpts={{
              useTextarea: true,
              label: intl.formatMessage(messages.recommendPrivate),
            }}
            placeholder={intl.formatMessage(messages.recommendPlaceholder)}
            maxLength="250"
          />
          <div className={classes.submit}>
            <PrimaryButton
              aria-disabled={!values.user || !values.message}
              isSubmitting={isSubmitting}
            >
              {title}
            </PrimaryButton>
          </div>
        </Form>
      )}
    </Formik>
  );

  const step2 = selectedUser && (
    <div className={classes.messageConfirmation}>
      {intl.formatMessage(messages.recommendSentMessage, {
        fullName: getUserFullName(selectedUser),
      })}
    </div>
  );

  return (
    <>
      <Modal
        handleClose={onClose}
        hideActions
        width="40em"
        title={title}
        icon={<RecommendIcon />}
        showClose
        content={
          <div>
            <div className={classes.body}>
              {step === 1 && step1}
              {step === 2 && step2}
            </div>
          </div>
        }
      />
    </>
  );
};

export default React.memo<Props>(RecommendationModal);
